import { PolicyDefinition } from "./policyTypes";

export const polSafeguarding: PolicyDefinition[] = [
  {
    id: "saf-recognition",
    code: "SAF-001",
    title: "Safeguarding Adults: Recognition and Prevention of Abuse",
    category: "safeguarding",
    cqcFocus: "Safe",
    sop: false,
    version: "4.0",
    reviewMonths: 12,
    summary: "Sets a zero tolerance approach to abuse and neglect and ensures every member of staff can recognise the signs and knows the duty to report.",
    keyPoints: [
      "Abuse can be physical, sexual, psychological, financial, neglect, institutional or modern slavery.",
      "Signs include unexplained injuries, fear, withdrawal, missing money or poor hygiene.",
      "Concerns are reported the same day; nothing is promised to be kept secret.",
      "Evidence is preserved and never cleaned away.",
    ],
    relatedRegulations: [
      "Care Act 2014",
      "Regulation 13 Safeguarding Service Users from Abuse and Improper Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Know the types of abuse and the signs for each.",
        "2. Listen to the person and record their words; do not promise secrecy.",
        "3. Report to the safeguarding lead the same day, verbally and in writing.",
        "4. Preserve evidence: do not clean, tidy or dispose of anything relevant.",
        "5. Complete the safeguarding concern form with facts only.",
        "6. Cooperate fully with any enquiry.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Every staff member, including agency and students, has a duty to report.",
        "Registered Manager is the safeguarding lead; Deputy is the alternate.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Safeguarding training completed by all staff every year.",
        "Quarterly scenario refresher in team meetings.",
      ]},
    ],
  },
  {
    id: "saf-response",
    code: "SAF-002",
    title: "Safeguarding Response, Referral and Enquiry SOP",
    category: "safeguarding",
    cqcFocus: "Safe",
    sop: true,
    version: "4.0",
    reviewMonths: 12,
    summary: "Step by step response when a safeguarding concern is raised, from immediate safety to local authority referral, interim protection and closure with learning.",
    keyPoints: [
      "Immediate safety and medical needs come first.",
      "Local authority referral is made within one working day.",
      "CQC is notified where the event is notifiable.",
      "The person and family are kept informed throughout.",
    ],
    relatedRegulations: [
      "Care Act 2014",
      "Regulation 18 Notification of Incidents",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Make the person safe and arrange medical help if needed.",
        "2. Inform the safeguarding lead immediately.",
        "3. Refer to the local authority within one working day.",
        "4. Notify CQC where the event meets notification criteria.",
        "5. Put interim protective measures in place and record them.",
        "6. Keep the person and their family informed at every stage.",
        "7. Attend and contribute to strategy meetings.",
        "8. Record closure, outcomes and shared learning.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Safeguarding lead owns the referral and the record.",
        "Deputy Manager maintains the safeguarding log.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Safeguarding log reviewed monthly with the manager.",
        "Every enquiry tracked to closure with actions completed.",
      ]},
    ],
  },
  {
    id: "saf-whistleblowing",
    code: "SAF-003",
    title: "Whistleblowing and Raising Concerns",
    category: "safeguarding",
    cqcFocus: "Well led",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Enables any worker to raise concerns in confidence, without fear of victimisation, and escalates to the CQC where concerns are not addressed.",
    keyPoints: [
      "Concerns can be raised in confidence and are acknowledged within 5 days.",
      "No worker suffers detriment for raising a genuine concern.",
      "If not addressed internally, staff may go direct to CQC.",
      "Outcomes are fed back to the person who raised the concern.",
    ],
    relatedRegulations: [
      "Public Interest Disclosure Act 1998",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Raise the concern with your line manager in the first instance.",
        "2. If that is not appropriate, raise it with the Registered Manager or director.",
        "3. If the concern is not addressed, contact CQC or the relevant regulator direct.",
        "4. The concern is acknowledged in writing within 5 working days.",
        "5. An investigation is carried out, independently where needed.",
        "6. The outcome and any actions are fed back to the whistleblower.",
        "7. Any victimisation is treated as gross misconduct.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Managers must never suppress or ignore a concern.",
        "Registered Manager reports serious concerns to the director and CQC.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual staff survey includes confidence to speak up.",
        "Raising concerns training refreshed annually.",
      ]},
    ],
  },
  {
    id: "saf-restriction",
    code: "SAF-004",
    title: "Restrictive Practice and Restraint",
    category: "safeguarding",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Restraint is used only as a last resort, must be proportionate and for the shortest time, and every use is recorded, reviewed and reduced.",
    keyPoints: [
      "De-escalation is always tried before any restraint.",
      "Restraint is never used as punishment or for convenience.",
      "Every use is recorded within one hour, including alternatives tried.",
      "Each case is reviewed and a reduction plan maintained.",
    ],
    relatedRegulations: [
      "Mental Capacity Act 2005",
      "Regulation 13 Safeguarding Service Users from Abuse and Improper Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Use de-escalation and distraction first.",
        "2. Restrain only to prevent harm, using the least force.",
        "3. Keep restraint proportionate and for the shortest possible time.",
        "4. Record the event within one hour, including alternatives tried.",
        "5. Inform the manager and check the physical health of the person.",
        "6. Inform family or representatives where appropriate.",
        "7. Debrief the person and the staff involved.",
        "8. Review the event and update the restriction reduction plan.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Staff using restraint must be trained in the approved method.",
        "Registered Manager reviews every record personally.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly review of all restraint records.",
        "Trend analysis feeds the reduction plan.",
      ]},
    ],
  },
  {
    id: "saf-allegations",
    code: "SAF-005",
    title: "Allegations Against Staff and Volunteers",
    category: "safeguarding",
    cqcFocus: "Well led",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Allegations against people who work here are managed swiftly and fairly, with the resident protected first and LADO involved where criteria are met.",
    keyPoints: [
      "Immediate safety is secured before anything else.",
      "LADO is referred within one working day where criteria are met.",
      "Suspension is a last resort after risk assessment.",
      "DBS referrals are made where the criteria apply.",
    ],
    relatedRegulations: [
      "Care Act 2014",
      "Local safeguarding adults procedures",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Secure the immediate safety of residents.",
        "2. Inform the Registered Manager the same day.",
        "3. Complete a risk assessment and decide on duties; redeploy before suspending.",
        "4. Refer to the LADO within one working day where criteria are met.",
        "5. Notify CQC where the event is notifiable.",
        "6. Cooperate with the investigation and keep records confidential.",
        "7. Record the outcome; refer to DBS where criteria are met.",
        "8. Capture learning and offer support to the staff member.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Registered Manager owns the allegation record.",
        "Director is informed of any allegation against the manager.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Allegations log maintained and reviewed quarterly.",
        "Lessons shared in supervision without identifying details.",
      ]},
    ],
  },
];